import { MutableRefObject, RefObject, useEffect, useState } from 'react';
import { useBreadcrumbTruncationContext } from './BreadcrumbTruncationContext';
import { getBreadcrumbContentWidths, getRefWidth } from './breadcrumbsHelper';
import { throttle } from '@amzn/storm-ui-utils';
// Self-import the module namespace so the hook invokes the *exported*
// calculateTruncationIndex / useBreadcrumbResizeListener bindings. This lets
// tests replace them via a module spy; a direct local call would bind to the
// function value at definition time and could not be intercepted.
import * as self from './useBreadcrumbTruncationHandler';

const WINDOW_RESIZE_THROTTLING_TIMEOUT = 150;

type UseBreadcrumbResizeListenerProps = {
  container: RefObject<HTMLDivElement>;
  truncationFunction: VoidFunction;
};

export const useBreadcrumbResizeListener = ({
  container,
  truncationFunction,
}: UseBreadcrumbResizeListenerProps) => {
  // handle window resize event
  useEffect(() => {
    const throttledTruncateFunction = throttle(() => {
      if (container.current) {
        truncationFunction();
      }
    }, WINDOW_RESIZE_THROTTLING_TIMEOUT);
    window.addEventListener('resize', throttledTruncateFunction);
    return () => {
      window.removeEventListener('resize', throttledTruncateFunction);
    };
  }, []);
};

export enum BreadcrumbPlacement {
  ContextBar,
  BelowHeader,
}

export const calculateTruncationIndex = ({
  breadcrumbRefs,
  containerRef,
  caretIconRef,
}: {
  breadcrumbRefs: MutableRefObject<HTMLElement[] | null>;
  containerRef: RefObject<HTMLDivElement>;
  caretIconRef: RefObject<HTMLElement | null>;
}) => {
  if (!breadcrumbRefs.current) return -1;

  const breadcrumbsTotalWidths = getBreadcrumbContentWidths(
    breadcrumbRefs.current,
  );
  const outerWidth = getRefWidth(containerRef);
  const outerHeight = containerRef?.current?.offsetHeight ?? -1;
  const caretIconWidth = getRefWidth(caretIconRef);

  const outerWidthNoCaret =
    outerHeight <= 0 ? outerWidth - caretIconWidth : outerWidth;
  const untruncateIndex = breadcrumbsTotalWidths.findIndex(
    (width) => width > outerWidthNoCaret,
  );

  return untruncateIndex > -1
    ? breadcrumbRefs.current.length - 1 - untruncateIndex
    : -1;
};

export const useBreadcrumbTruncation = ({
  containerRef,
}: {
  containerRef: RefObject<HTMLDivElement>;
}) => {
  const [truncationIndex, setTruncationIndex] = useState<number>(-1);
  const { caretIconRef, breadcrumbRefs } = useBreadcrumbTruncationContext();

  const handleTruncation = () => {
    const newTruncationIndex = self.calculateTruncationIndex({
      breadcrumbRefs,
      containerRef,
      caretIconRef,
    });
    setTruncationIndex(newTruncationIndex);
  };

  self.useBreadcrumbResizeListener({
    container: containerRef,
    truncationFunction: handleTruncation,
  });

  return {
    handleTruncation,
    truncationIndex,
  };
};
